import type { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

import type {
  CompletedCalculationTactic,
  LoadFlowMergedScenariosResult,
  LoadFlowScenarioInputs
} from '../../../..';
import type { ForStoringAttentionStatus } from '../../../../evaluation/AttentionStatusRepository';
import { BusbarEvaluation } from '../../../../evaluation/loadFlow/gridElements/busbar/busbarEvaluation';
import { ResultsFilter } from '../../../../filterOnlyMvBusbarResults';
import type { FunctionalGrid } from '../../../../grid/FunctionalGrid';
import {
  findGridElementLabel,
  findGridElementTitle
} from '../../findGridElementLabel';
import type { ForCreatingEvaluationEngines } from '../ForCreatingEvaluationEngines';
import { findLoad } from '../findLoad';
import type { BusbarLoadFlowDetails } from '../gridElementLoadFlowDetails.types';
import { Features } from '../../../../createGridElementCalculationSetExecutionDetails';

export function createMvBusbarsLoadFlowDetails(
  features: Features,
  evaluationEngineFactory: ForCreatingEvaluationEngines,
  results: LoadFlowMergedScenariosResult[],
  functionalGrid: FunctionalGrid,
  attentionStatusRepository: ForStoringAttentionStatus
): BusbarLoadFlowDetails[] {
  const filteredResults = ResultsFilter.Create(results, functionalGrid)
    .removeLvBusbarsFromResults()
    .removeExternalNetworkInjectionFromBusbarResults()
    .toResults();

  const evaluatedResults = BusbarEvaluation.fromResults(
    filteredResults,
    attentionStatusRepository
  ).evaluateEachBusbar(evaluationEngineFactory.createBusbarEvaluationEngine());

  return evaluatedResults.map(evaluatedAcLine => ({
    ...evaluatedAcLine,
    label: findGridElementLabel(features, evaluatedAcLine.id),
    title: findGridElementTitle(features, evaluatedAcLine.id),
    inputs: undefined
  }));
}

export function createLvBusbarsLoadFlowDetails(
  features: Features,
  evaluationEngineFactory: ForCreatingEvaluationEngines,
  results: LoadFlowMergedScenariosResult[],
  calculationTactic: CompletedCalculationTactic,
  functionalGrid: FunctionalGrid,
  attentionStatusRepository: ForStoringAttentionStatus,
  inputs: LoadFlowScenarioInputs
): BusbarLoadFlowDetails[] {
  const filteredResults = ResultsFilter.Create(results, functionalGrid)
    .removeMvBusbarsFromResults()
    .toResults();

  const evaluatedResults = BusbarEvaluation.fromResults(
    filteredResults,
    attentionStatusRepository
  ).evaluateEachBusbar(evaluationEngineFactory.createBusbarEvaluationEngine());

  return evaluatedResults.map(busbar => ({
    ...busbar,
    label: findGridElementLabel(features, busbar.id),
    inputs: findLoad(busbar.id, calculationTactic, inputs)
  }));
}
